import React, { useMemo } from 'react';

const FilmDetailRow = React.memo(({
    film,
    index,
    onUpdate,
    onRemove,
    onAdd,
    filmSizes,
    observations,
    remarkOptions
}) => {
    const isValidFilmSize = useMemo(() => {
        // If no film size selected, it's valid (not an error state)
        if (!film.filmSize || film.filmSize.trim() === '') {
            return true;
        }

        // If filmSizes hasn't loaded yet, assume valid (don't show false error)
        if (!filmSizes || filmSizes.length === 0) {
            return true;
        }

        // Normalize both values for comparison (trim + lowercase)
        const normalizedInput = film.filmSize.trim().toLowerCase();

        // Check if any film size matches
        return filmSizes.some(size => {
            const normalizedSize = `${size.film_size}-${size.film_brand}`.trim().toLowerCase();
            return normalizedSize === normalizedInput;
        });
    }, [film.filmSize, filmSizes]);

    const isSequenceMissing = !film.sequence || film.sequence.trim() === '';

    return (
        <tr className="border-b hover:bg-gray-50">
            <td className="px-3 py-2 border border-gray-300 text-center">{index + 1}</td>

            {/* Film Size with FIXED validation */}
            <td className="px-3 py-2 border border-gray-300">
                <input
                    type="text"
                    list={`filmsize-${film.id}`}
                    value={film.filmSize}
                    onChange={(e) => onUpdate(film.id, 'filmSize', e.target.value)}
                    className={`w-full px-2 py-1 text-sm border rounded focus:ring-1 focus:border-transparent ${film.filmSize && !isValidFilmSize
                        ? 'border-red-500 bg-red-50 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                        }`}
                    placeholder="Search or select..."
                />
                <datalist id={`filmsize-${film.id}`}>
                    {filmSizes.map((size) => (
                        <option key={size.id} value={`${size.film_size}-${size.film_brand}`}>
                            {size.film_size}-{size.film_brand}
                        </option>
                    ))}
                </datalist>
                {film.filmSize && !isValidFilmSize && (
                    <div className="text-xs text-red-600 font-semibold mt-1 flex items-center">
                        <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Film size not available in system
                    </div>
                )}
            </td>

            {/* Joint ID */}
            <td className="px-3 py-2 border border-gray-300">
                <input
                    type="text"
                    value={film.jointId}
                    onChange={(e) => onUpdate(film.id, 'jointId', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Joint identification"
                />
            </td>

            {/* Sequence */}
            <td className="px-3 py-2 border border-gray-300">
                <input
                    type="text"
                    value={film.sequence}
                    onChange={(e) => onUpdate(film.id, 'sequence', e.target.value)}
                    className={`w-full px-2 py-1 text-sm border rounded focus:ring-1 focus:border-transparent ${isSequenceMissing
                        ? 'border-red-500 bg-red-50 focus:ring-red-500'
                        : 'border-gray-300 focus:ring-blue-500'
                        }`}
                    placeholder=""
                    required
                />
                {isSequenceMissing && (
                    <div className="text-xs text-red-600 font-semibold mt-1">
                        Required field
                    </div>
                )}
            </td>

            {/* Observation */}
            <td className="px-3 py-2 border border-gray-300">
                <input
                    type="text"
                    value={film.observation}
                    onChange={(e) => onUpdate(film.id, 'observation', e.target.value)}
                    list={`observations-${film.id}`}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                />
                <datalist id={`observations-${film.id}`}>
                    {observations.map((obs) => (
                        <option key={obs.id} value={obs.observation_text} />
                    ))}
                </datalist>
            </td>

            {/* Remark */}
            <td className="px-3 py-2 border border-gray-300">
                <select
                    value={film.remark}
                    onChange={(e) => onUpdate(film.id, 'remark', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                >
                    {remarkOptions.map((remark) => (
                        <option key={remark} value={remark}>{remark}</option>
                    ))}
                </select>
            </td>

            {/* Actions */}
            <td className="px-3 py-2 border border-gray-300">
                <div className="flex justify-center space-x-1">
                    <button
                        type="button"
                        onClick={() => onRemove(film.id)}
                        className="bg-red-600 text-white w-7 h-7 rounded hover:bg-red-700 transition text-lg flex items-center justify-center"
                        title="Remove"
                    >
                        ×
                    </button>
                    <button
                        type="button"
                        onClick={() => onAdd(film.id)}
                        className="bg-indigo-900 text-white w-7 h-7 rounded hover:bg-blue-600 transition flex items-center justify-center"
                        title="Add"
                    >
                        +
                    </button>
                </div>
            </td>
        </tr>
    );
}, (prevProps, nextProps) => {
    // Only re-render if THIS film's data changed
    return (
        prevProps.film.filmSize === nextProps.film.filmSize &&
        prevProps.film.jointId === nextProps.film.jointId &&
        prevProps.film.sequence === nextProps.film.sequence &&
        prevProps.film.observation === nextProps.film.observation &&
        prevProps.film.remark === nextProps.film.remark &&
        prevProps.index === nextProps.index
    );
});

FilmDetailRow.displayName = 'FilmDetailRow';

export default FilmDetailRow;